import { createTheme, alpha } from '@mui/material/styles';

// ─── IQMaxx Design Tokens (from Figma Style Guide) ───────────────────────────

export const colors = {
  // Primary – brand blue
  primary: {
    lightest: '#eaf2fb',
    light: '#77ace9',
    main: '#4a90e2',
    dark: '#357abd',
    contrastText: '#ffffff',
  },

  // Secondary – purple (seen in checkboxes, tabs, switches, menus, calendar)
  secondary: {
    lightest: '#f3e8ff',
    light: '#c084fc',
    main: '#9747ff',
    dark: '#7c3aed',
    contrastText: '#ffffff',
  },

  // Green – success / kid selection CTA
  green: {
    lightest: '#ebfde8',
    light: '#7ed321',
    main: '#5ba617',
    dark: '#3f7410',
    contrastText: '#ffffff',
  },

  // Semantic states
  error: {
    light: '#ffbfbf',
    main: '#ff6b6b',
    dark: '#cb4242',
    contrastText: '#ffffff',
  },
  warning: {
    light: '#fff3c4',
    main: '#ffd93d',
    dark: '#f5a623',
    contrastText: '#333333',
  },
  info: {
    light: '#eaf2fb',
    main: '#4a90e2',
    dark: '#357abd',
    contrastText: '#ffffff',
  },
  success: {
    light: '#ebfde8',
    main: '#7ed321',
    dark: '#5ba617',
    contrastText: '#ffffff',
  },

  // Orange accent (CTAs, highlights)
  accent: '#f5a623',

  // Dark navy (sidebar, dark surface)
  navy: '#1a2225',

  // Neutrals
  grey: {
    50: '#f7f9fc',
    100: '#edf0f5',
    200: '#e5e5e5',
    300: '#d9d9d9',
    400: '#cccccc',
    500: '#b3b3b3',
    600: '#999999',
    700: '#808080',
    800: '#666666',
    900: '#4d4d4d',
  },

  // Text
  text: {
    primary: '#333333',
    secondary: '#666666',
    disabled: '#999999',
    inverse: '#ffffff',
  },
};

// ─── MUI Theme ────────────────────────────────────────────────────────────────

const theme = createTheme({
  // ── Palette ──────────────────────────────────────────────────────────────
  palette: {
    primary: {
      light: colors.primary.light,
      main: colors.primary.main,
      dark: colors.primary.dark,
      contrastText: colors.primary.contrastText,
    },
    secondary: {
      light: colors.secondary.light,
      main: colors.secondary.main,
      dark: colors.secondary.dark,
      contrastText: colors.secondary.contrastText,
    },
    error: {
      light: colors.error.light,
      main: colors.error.main,
      dark: colors.error.dark,
      contrastText: colors.error.contrastText,
    },
    warning: {
      light: colors.warning.light,
      main: colors.warning.main,
      dark: colors.warning.dark,
      contrastText: colors.warning.contrastText,
    },
    info: {
      light: colors.info.light,
      main: colors.info.main,
      dark: colors.info.dark,
      contrastText: colors.info.contrastText,
    },
    success: {
      light: colors.success.light,
      main: colors.success.main,
      dark: colors.success.dark,
      contrastText: colors.success.contrastText,
    },
    grey: colors.grey as Record<string, string> & {
      50: string;
      100: string;
      200: string;
      300: string;
      400: string;
      500: string;
      600: string;
      700: string;
      800: string;
      900: string;
    },
    text: {
      primary: colors.text.primary,
      secondary: colors.text.secondary,
      disabled: colors.text.disabled,
    },
    background: {
      default: colors.grey[50],
      paper: '#ffffff',
    },
    divider: colors.grey[200],
  },

  // ── Typography ────────────────────────────────────────────────────────────
  // Inter is the primary font (loaded via index.html Google Fonts)
  typography: {
    fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
    fontWeightLight: 300,
    fontWeightRegular: 400,
    fontWeightMedium: 500,
    fontWeightBold: 700,

    h1: {
      fontSize: '2.5rem',
      fontWeight: 700,
      lineHeight: 1.2,
      letterSpacing: '-0.02em',
      color: colors.text.primary,
    },
    h2: {
      fontSize: '2rem',
      fontWeight: 700,
      lineHeight: 1.25,
      letterSpacing: '-0.01em',
      color: colors.text.primary,
    },
    h3: { fontSize: '1.75rem', fontWeight: 600, lineHeight: 1.3, color: colors.text.primary },
    h4: { fontSize: '1.5rem', fontWeight: 600, lineHeight: 1.35, color: colors.text.primary },
    h5: { fontSize: '1.25rem', fontWeight: 600, lineHeight: 1.4, color: colors.text.primary },
    h6: { fontSize: '1rem', fontWeight: 600, lineHeight: 1.5, color: colors.text.primary },

    subtitle1: { fontSize: '1rem', fontWeight: 500, lineHeight: 1.5, color: colors.text.secondary },
    subtitle2: {
      fontSize: '0.875rem',
      fontWeight: 500,
      lineHeight: 1.57,
      color: colors.text.secondary,
    },

    body1: { fontSize: '1rem', fontWeight: 400, lineHeight: 1.6, color: colors.text.primary },
    body2: { fontSize: '0.875rem', fontWeight: 400, lineHeight: 1.6, color: colors.text.secondary },

    button: {
      fontSize: '0.875rem',
      fontWeight: 600,
      lineHeight: 1.75,
      textTransform: 'none' as const,
    },
    caption: {
      fontSize: '0.75rem',
      fontWeight: 400,
      lineHeight: 1.66,
      color: colors.text.secondary,
    },
    overline: {
      fontSize: '0.6875rem',
      fontWeight: 600,
      lineHeight: 2.66,
      letterSpacing: '0.08em',
      textTransform: 'uppercase' as const,
    },
  },

  // ── Spacing (base 8px) ────────────────────────────────────────────────────
  spacing: 8,

  // ── Shape ─────────────────────────────────────────────────────────────────
  shape: {
    borderRadius: 8,
  },

  // ── Shadows ───────────────────────────────────────────────────────────────
  shadows: [
    'none',
    '0 1px 3px rgba(0,0,0,0.06), 0 1px 2px rgba(0,0,0,0.04)',
    '0 2px 8px rgba(0,0,0,0.08)',
    '0 4px 16px rgba(0,0,0,0.10)',
    '0 6px 20px rgba(0,0,0,0.10)',
    '0 8px 24px rgba(0,0,0,0.12)',
    '0 10px 28px rgba(0,0,0,0.12)',
    '0 12px 32px rgba(0,0,0,0.14)',
    '0 14px 36px rgba(0,0,0,0.14)',
    '0 16px 40px rgba(0,0,0,0.16)',
    '0 18px 44px rgba(0,0,0,0.16)',
    '0 20px 48px rgba(0,0,0,0.18)',
    '0 22px 52px rgba(0,0,0,0.18)',
    '0 24px 56px rgba(0,0,0,0.20)',
    '0 26px 60px rgba(0,0,0,0.20)',
    '0 28px 64px rgba(0,0,0,0.22)',
    '0 30px 68px rgba(0,0,0,0.22)',
    '0 32px 72px rgba(0,0,0,0.24)',
    '0 34px 76px rgba(0,0,0,0.24)',
    '0 36px 80px rgba(0,0,0,0.26)',
    '0 38px 84px rgba(0,0,0,0.26)',
    '0 40px 88px rgba(0,0,0,0.28)',
    '0 42px 92px rgba(0,0,0,0.28)',
    '0 44px 96px rgba(0,0,0,0.30)',
    '0 48px 100px rgba(0,0,0,0.32)',
  ],

  // ── Component Overrides ───────────────────────────────────────────────────
  components: {
    // --- Button ---
    MuiButton: {
      defaultProps: {
        disableElevation: true,
      },
      styleOverrides: {
        root: {
          borderRadius: 8,
          textTransform: 'none',
          fontWeight: 600,
          fontSize: '0.875rem',
          padding: '10px 20px',
          transition: 'background-color 0.2s ease, box-shadow 0.2s ease',
        },
        sizeLarge: { padding: '12px 28px', fontSize: '1rem' },
        sizeSmall: { padding: '6px 14px', fontSize: '0.8125rem' },
        contained: {
          '&:hover': { boxShadow: '0 4px 12px rgba(74,144,226,0.35)' },
        },
        containedSecondary: {
          '&:hover': { boxShadow: '0 4px 12px rgba(151,71,255,0.35)' },
        },
        outlined: {
          borderWidth: '1.5px',
          '&:hover': { borderWidth: '1.5px' },
        },
      },
    },

    // --- IconButton ---
    MuiIconButton: {
      styleOverrides: {
        root: { borderRadius: 8 },
      },
    },

    // --- Card ---
    MuiCard: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: {
          borderRadius: 12,
          border: `1px solid ${colors.grey[200]}`,
          boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
          '&:hover': { boxShadow: '0 4px 16px rgba(0,0,0,0.10)' },
          transition: 'box-shadow 0.2s ease',
        },
      },
    },
    MuiCardContent: {
      styleOverrides: {
        root: { padding: '20px', '&:last-child': { paddingBottom: '20px' } },
      },
    },

    // --- Paper ---
    MuiPaper: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: { backgroundImage: 'none' },
        outlined: { borderColor: colors.grey[200] },
        rounded: { borderRadius: 12 },
      },
    },

    // --- TextField / Input ---
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          backgroundColor: '#ffffff',
          '& .MuiOutlinedInput-notchedOutline': { borderColor: colors.grey[300] },
          '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: colors.primary.main },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: colors.primary.main,
            borderWidth: '1.5px',
          },
          '&.Mui-error .MuiOutlinedInput-notchedOutline': { borderColor: colors.error.main },
        },
        input: { padding: '12px 14px' },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          fontSize: '0.875rem',
          '&.Mui-focused': { color: colors.primary.main },
        },
      },
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: { fontSize: '0.75rem', marginTop: 4 },
      },
    },

    // --- Chip ---
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 6,
          fontWeight: 500,
          fontSize: '0.75rem',
          height: 28,
        },
        colorPrimary: {
          backgroundColor: colors.primary.lightest,
          color: colors.primary.dark,
        },
        colorSecondary: {
          backgroundColor: colors.secondary.lightest,
          color: colors.secondary.dark,
        },
      },
    },

    // --- Tabs ---
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 500,
          fontSize: '0.875rem',
          minWidth: 0,
          padding: '10px 16px',
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        indicator: { height: 3, borderRadius: '3px 3px 0 0' },
      },
    },

    // --- Table ---
    MuiTableHead: {
      styleOverrides: {
        root: {
          backgroundColor: colors.grey[50],
          '& .MuiTableCell-root': {
            fontWeight: 600,
            color: colors.text.secondary,
            fontSize: '0.75rem',
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: { borderColor: colors.grey[200], padding: '12px 16px' },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          '&:hover': { backgroundColor: colors.primary.lightest },
          transition: 'background-color 0.15s ease',
        },
      },
    },

    // --- Alert ---
    MuiAlert: {
      styleOverrides: {
        root: { borderRadius: 8 },
        standardInfo: { backgroundColor: colors.primary.lightest, color: colors.primary.dark },
        standardSuccess: { backgroundColor: colors.success.light, color: colors.success.dark },
        standardWarning: { backgroundColor: colors.warning.light, color: colors.text.primary },
        standardError: { backgroundColor: colors.error.light, color: colors.error.dark },
      },
    },

    // --- Badge ---
    MuiBadge: {
      styleOverrides: {
        badge: { fontWeight: 700, fontSize: '0.6875rem' },
      },
    },

    // --- Avatar ---
    MuiAvatar: {
      styleOverrides: {
        root: {
          backgroundColor: colors.primary.main,
          color: '#ffffff',
          fontWeight: 600,
        },
      },
    },

    // --- Tooltip ---
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: colors.navy,
          fontSize: '0.75rem',
          fontWeight: 500,
          borderRadius: 6,
          padding: '6px 10px',
        },
        arrow: { color: colors.navy },
      },
    },

    // --- Dialog ---
    MuiDialog: {
      styleOverrides: {
        paper: { borderRadius: 16 },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: { fontSize: '1.125rem', fontWeight: 600, padding: '20px 24px 12px' },
      },
    },
    MuiDialogContent: {
      styleOverrides: {
        root: { padding: '12px 24px' },
      },
    },
    MuiDialogActions: {
      styleOverrides: {
        root: { padding: '12px 24px 20px', gap: 8 },
      },
    },

    // --- Drawer (sidebar) ---
    MuiDrawer: {
      styleOverrides: {
        paper: { borderRight: 'none', backgroundColor: colors.navy },
      },
    },

    // --- AppBar ---
    MuiAppBar: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: {
          backgroundColor: '#ffffff',
          color: colors.text.primary,
          borderBottom: `1px solid ${colors.grey[200]}`,
        },
      },
    },

    // --- LinearProgress ---
    MuiLinearProgress: {
      styleOverrides: {
        root: { borderRadius: 4, height: 6, backgroundColor: colors.grey[200] },
        bar: { borderRadius: 4 },
      },
    },

    // --- Skeleton ---
    MuiSkeleton: {
      styleOverrides: {
        root: { backgroundColor: colors.grey[200] },
      },
    },

    // --- Divider ---
    MuiDivider: {
      styleOverrides: {
        root: { borderColor: colors.grey[200] },
      },
    },

    // --- Link ---
    MuiLink: {
      defaultProps: { underline: 'hover' },
      styleOverrides: {
        root: { color: colors.primary.main, fontWeight: 500 },
      },
    },

    // --- Breadcrumbs ---
    MuiBreadcrumbs: {
      styleOverrides: {
        li: { fontSize: '0.875rem' },
        separator: { color: colors.grey[500] },
      },
    },

    // --- List ---
    MuiListItemButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          '&.Mui-selected': {
            backgroundColor: alpha(colors.primary.main, 0.12),
            color: colors.primary.main,
            '&:hover': { backgroundColor: alpha(colors.primary.main, 0.18) },
          },
          '&:hover': { backgroundColor: alpha(colors.primary.main, 0.06) },
        },
      },
    },
  },
});

export default theme;
